import React, { useEffect, useState } from "react";
import { BiSearch } from "react-icons/bi";
import Pagination from "../pagination";
import Table from "../table";
import getInitialsMessage from "../../utilities/messages/getInitialsMessage";
import { EUIcon, GBicon, NGIcon, TradeVuIcon, TradevuIcon, USIcon } from "../../asset/icons";
import SortDropdown from "./SortDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
    handleDeleteBeneficiary,
    handleGetWalletBeneficiaries,
    fetchTransferFees,
} from "../../api/fetchers";
import { Send2, Trash } from "iconsax-reactjs";
import EmptyBeneficiary from "../../asset/images/EmptyBeneficiaryState.svg";
import Loader from "../loader";
import BeneficiaryDetailsDrawer from "./BeneficiaryDetailsDrawer";
import NoMatchingBeneficiaryImg from "../../asset/images/NoMatchingTransaction.svg";
import DeleteModal from "../DeleteModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import SendMoneyModal from "./SendMoneyBeneficiaryModal";
import {
    setBankAmount,
    setBankCurrency,
    setBankRecipient,
} from "../../redux/slices/bankTransferSlice";
import { setUsdAll, setUsdBeneficiaryId } from "../../redux/slices/usdTransferSlice";
import MoreOptionsDropdown from "../flex/shipments/modals/MoreOptionsDropdown";
import {
    normalizeBankRecipient,
    normalizeUsdRecipientForStore,
} from "../../utilities/normalizeBeneficiaries";

export default function BeneficiariesSection({ onCountChange }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { business, currency: defaults } = useSelector((s) => s);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [sortBy, setSortBy] = useState("recent");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [selected, setSelected] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [beneficiaryForSend, setBeneficiaryForSend] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);
    const [fxFeeRate, setFxFeeRate] = useState(0);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 700);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        if (!business?._id || !defaults) return;
        (async () => {
            setLoading(true);
            try {
                const data = await handleGetWalletBeneficiaries({
                    businessId: business._id,
                    currency: defaults,
                    sortBy,
                    search: debouncedSearchTerm,
                });
                setBeneficiaries(data || []);
                onCountChange?.((data || []).length);
            } catch {
                setBeneficiaries([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [business?._id, defaults, sortBy, debouncedSearchTerm]);

    useEffect(() => {
        if (!business?._id) return;
        let cancelled = false;
        (async () => {
            try {
                setFeeLoading(true);
                const fees = await fetchTransferFees({ businessId: business._id });
                const fx = Number(fees?.fxTransferFee ?? fees?.data?.fxTransferFee ?? 0);
                if (!cancelled) setFxFeeRate(Number.isFinite(fx) ? fx : 0);
            } catch {
                if (!cancelled) setFxFeeRate(0);
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [business?._id]);

    useEffect(() => {
        const onDocClick = () => setOpenMenuId(null);
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const noFiltersApplied = !debouncedSearchTerm;

    const onConfirmDelete = async () => {
        if (!toDelete) return;
        setDeleteLoading(true);

        try {
            await handleDeleteBeneficiary({
                businessId: business._id,
                beneficiaryId: toDelete._id,
            });
            setBeneficiaries((prev) => prev.filter((b) => b._id !== toDelete._id));
            onCountChange?.((prev) => prev - 1);
            toast.success("Beneficiary deleted");
        } catch (err) {
            console.error(err);
            toast.error(err.message ?? "Failed to delete beneficiary");
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
            setToDelete(null);
        }
    };

    const getTransferIcon = (type) => {
        switch (type) {
            case "NGN":
                return <NGIcon />;
            case "EUR":
                return <EUIcon />;
            case "GBP":
                return <GBicon />;
            case "USD":
                return <USIcon />;
            case "TRADEVU":
                return <TradeVuIcon />;
            default:
                return <TradevuIcon />;
        }
    };

    if (loading) {
        return (
            <div className="py-20 text-center">
                <button className="bg-[#6F00FF] px-4 py-2 rounded-lg">
                    <Loader />
                </button>
            </div>
        );
    }
    if (!loading && beneficiaries.length === 0 && noFiltersApplied) {
        return (
            <div className="text-center bg-white py-52">
                <img src={EmptyBeneficiary} alt="No beneficiaries" className="w-32 mx-auto mb-3" />
                <p className="text-[#101828] font-semibold">No saved beneficiaries</p>
                <p className="text-sm text-[#6F6B7B]">
                    You can save a beneficiary after sending money on TradeVu.
                </p>
                <button
                    className="py-2 border border-[#6F739B] mt-4 text-[#161827] rounded-lg px-4 space-x-3"
                    onClick={() => {}}
                >
                    <Send2 size={20} /> Send money
                </button>
            </div>
        );
    }

    const paginated = beneficiaries.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const rows = paginated.map((b) => {
        const isMenuOpen = openMenuId === b._id;
        const beneficiaryDropDown = ({ navigate }) => {
            const isCryptoWallet = ["USDT", "USDC"].includes(defaults);

            return [
                {
                    label: "Send money",
                    value: "send-money",
                    className:
                        "flex items-center gap-2 w-full px-4 py-[9px] hover:bg-main-grey/10 hover:rounded-xl font-light text-sm text-[#101828]",
                    icon: <Send2 size={18} />,
                    action: () => {
                        setBeneficiaryForSend(b);
                        setShowSendModal(true);
                        setOpenMenuId(null);
                    },
                },
                {
                    label: "Delete beneficiary",
                    value: "delete-beneficiary",
                    className:
                        "flex items-center gap-2 w-full px-4 py-[9px] hover:bg-main-grey/10 hover:rounded-xl font-light text-sm text-red-500",
                    icon: <Trash size={18} />,
                    action: () => {
                        setToDelete(b);
                        setShowDeleteModal(true);
                    },
                },
            ];
        };

        return {
            rowId: b._id,
            onRowClick: () => {
                setSelected(b);
                setOpenMenuId(null);
            },
            columns: [
                {
                    value: (
                        <div className="flex items-center gap-3">
                            <div className="relative h-9 w-9">
                                <div className="h-full w-full rounded-full bg-[#F4F2FC] flex items-center justify-center text-xs font-semibold text-[#101828]">
                                    {getInitialsMessage(b.name)}
                                </div>
                                <span className="absolute text-lg -bottom-1 -right-1">
                                    {getTransferIcon(b.transferType)}
                                </span>
                            </div>
                            <span className="text-sm font-medium text-[#101828]">{b.name}</span>
                        </div>
                    ),
                },
                {
                    value: b.bankName,
                    className: "text-[#344054] text-center flex items-center justify-start !pl-8",
                },
                {
                    value: b.accountNumber,
                    className: "text-[#344054]  flex items-center justify-start !pl-14",
                },
                {
                    value: new Date(b.createdAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    }),
                    className: "text-[#344054]  flex items-center justify-center !pl-16",
                },
                {
                    value: (
                        <div className="flex items-center justify-between w-full gap-5 text-sm font-normal">
                            <span className="text-gray-70">View</span>
                            <MoreOptionsDropdown
                                dropClassName="!w-56"
                                options={beneficiaryDropDown({ navigate })}
                            />
                        </div>
                    ),
                    className: "justify-end",
                },
            ],
        };
    });

    return (
        <>
            <div className="py-6 bg-white rounded-xl">
                {/* Top bar */}
                <div className="flex flex-col justify-between gap-3 px-4 mb-3 md:flex-row md:items-center md:gap-4 md:mb-4">
                    <div className="relative w-full max-w-sm">
                        <input
                            type="text"
                            placeholder="Search beneficiaries"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm bg-[#EFF0FA] text-[#101828] placeholder:text-gray-400"
                        />
                        <BiSearch className="absolute left-3 top-2.5 text-gray-500" size={18} />
                    </div>
                    <SortDropdown
                        selected={sortBy}
                        onApply={(value) => {
                            setSortBy(value);
                            setCurrentPage(1);
                        }}
                        options={[
                            { label: "Recent", value: "recent" },
                            { label: "Oldest", value: "oldest" },
                            { label: "A - Z", value: "a-z" },
                            { label: "Z - A", value: "z-a" },
                        ]}
                    />
                </div>

                {!loading && beneficiaries.length === 0 && debouncedSearchTerm ? (
                    <div className="text-center bg-white py-52">
                        <img
                            src={NoMatchingBeneficiaryImg}
                            alt="No matching beneficiary"
                            className="w-32 mx-auto mb-3"
                        />
                        <p className="text-[#101828] font-semibold">No matching beneficiary</p>
                        <p className="text-sm text-[#6F6B7B]">
                            Try adjusting your search or filters to find a beneficiary.
                        </p>
                    </div>
                ) : (
                    <Table
                        titles={[
                            { title: "Account name", width: "minmax(220px,1fr)" },
                            { title: "Bank name", width: "1fr" },
                            { title: "Account number", width: "1fr" },
                            { title: "Date added", width: "1fr" },
                            { title: "", width: "auto" },
                        ]}
                        rows={rows}
                        className="min-w-full"
                        wrapperClassName=""
                    />
                )}

                <DeleteModal
                    isOpen={showDeleteModal}
                    title="Delete beneficiary?"
                    description={`You are about to delete ${toDelete?.name} from your beneficiaries. This action cannot be undone.`}
                    confirmText="Delete beneficiary"
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={onConfirmDelete}
                    isLoading={deleteLoading}
                />

                {/* pagination */}
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={Math.ceil(beneficiaries.length / itemsPerPage)}
                />
            </div>

            {/* drawer */}
            {selected && (
                <BeneficiaryDetailsDrawer
                    beneficiary={selected}
                    onClose={() => setSelected(null)}
                    onDeleted={(id) => setBeneficiaries((prev) => prev.filter((b) => b._id !== id))}
                    businessId={business._id}
                />
            )}

            <SendMoneyModal
                isOpen={showSendModal}
                onClose={() => setShowSendModal(false)}
                beneficiary={beneficiaryForSend}
                currency={defaults}
                onContinue={({ amount, currency, beneficiary, rateKey }) => {
                    const cur = String(currency || defaults).toUpperCase();
                    const amt = String(amount);

                    if (["USDT", "USDC"].includes(cur)) {
                        dispatch({ type: "cryptoFiatTransfer/setCryptoFiatAmount", payload: amt });
                        dispatch({
                            type: "cryptoFiatTransfer/setCryptoFiatRecipient",
                            payload: {
                                institution: beneficiary.bankCode,
                                accountIdentifier: beneficiary.accountNumber,
                                accountName: beneficiary.name,
                                bankName: beneficiary.bankName,
                                toCurrency: beneficiary.currency ?? "NGN",
                            },
                        });
                        setShowSendModal(false);
                        navigate("/payment/send-crypto/fiat-wallet/payment-info");
                        return;
                    }

                    const bankRecipientPayload = normalizeBankRecipient(beneficiary);
                    dispatch(setBankRecipient(bankRecipientPayload));
                    dispatch(setBankAmount(amt));
                    dispatch(setBankCurrency(cur));

                    if (cur === "KES") {
                        setShowSendModal(false);
                        navigate("/payment/send-money/same-currency/bank-recipient/payment-info");
                        return;
                    }

                    if (cur === "USD") {
                        // Provider IDs
                        const bridgeId =
                            beneficiary?.bridgeBeneficiaryId || beneficiary?.beneficiaryId || "";
                        const listId = beneficiary?._id || "";

                        dispatch(setUsdBeneficiaryId(listId || bridgeId));

                        const usdPayload = normalizeUsdRecipientForStore(beneficiary);

                        dispatch(
                            setUsdAll({
                                amount: amt,
                                currency: "USD",
                                rateKey: String(rateKey || ""),
                                beneficiaryId: listId || bridgeId,
                                transactionId: "",
                                bridgeBeneficiaryId: bridgeId,

                                ...usdPayload,
                            })
                        );

                        setShowSendModal(false);
                        navigate("/payment/send-money/same-currency/bank-recipient/payment-info");
                        return;
                    }

                    // default (NGN/EUR/GBP/etc)
                    setShowSendModal(false);
                    navigate("/payment/send-money/same-currency/bank-recipient/payment-info");
                }}
            />
        </>
    );
}
